import React, { Component } from 'react';
import { get, flowRight } from 'lodash';

import { type WithTranslation } from '@wix/yoshi-flow-editor';
import {
  SECTION_CATEGORY,
  isLayoutProGallery,
  createPageUrl,
} from '@wix/communities-blog-client-common';
import stylesParams from '../../../../components/Blog/stylesParams';
import AnimatedLoader from '../../../common/components/animated-loader';
import withComponents, {
  type WithComponents,
} from '../../../common/components/components-provider/with-components';
import { EmptyLayout } from '../../../common/components/layout';
import Page from '../../../common/components/page';
import { connect } from '../../../common/components/runtime-context';
import Pagination from '../../../common/containers/pagination';
import { PostTitleTagProvider } from '../../../common/context/post-title-tag-context';
import withAuth, { type WithAuth } from '../../../common/hoc/with-auth';
import withLayoutProps, { type WithLayoutProps } from '../../../common/hoc/with-layout-props';
import withPermissions, { type WithPermissions } from '../../../common/hoc/with-permissions';
import withTranslate from '../../../common/hoc/with-translate';
import { isCreatePostButtonVisible } from '../../../common/selectors/blog-menu-button-selectors';
import {
  isExcludePostContentSupported,
  isPostContentRequired,
} from '../../../common/selectors/layout-selectors';
import { getSortedCategoryPosts, getPostsByPage } from '../../../common/selectors/post-selectors';
import { getHashtagUrl } from '../../../common/services/getHashtagUrl';
import { handleNavigation } from '../../../common/services/navigation';
import { getIsMobile, isSite } from '../../../common/store/basic-params/basic-params-selectors';
import { getIsHashtagPostsLoaded } from '../../../common/store/is-loaded/is-loaded-selectors';
import { getIsHashtagPostsLoading } from '../../../common/store/is-loading/is-loading-selectors';
import { getSectionUrl } from '../../../common/store/topology/topology-selectors';
import PostList from '../../components/post-list';

import withPaginationSettings, {
  type WithPaginationSettings,
} from '../../hoc/with-pagination-settings';

const Layout = EmptyLayout;

type Props = OwnProps &
  WithComponents &
  WithPermissions &
  WithPaginationSettings &
  WithLayoutProps &
  WithAuth &
  Pick<WithTranslation, 't'> &
  ReturnType<typeof mapRuntimeToProps>;

class HashtagPage extends Component<Props> {
  componentWillUnmount() {
    this.props.setIsLoaded('hashtag-posts', null, true);
  }

  componentDidUpdate({ isAuthenticated, isBlocked, pageSize, excludeContent }: Props) {
    const { hashtag } = this.props;
    if (
      this.props.isAuthenticated !== isAuthenticated ||
      this.props.isBlocked !== isBlocked ||
      this.props.pageSize !== pageSize ||
      (excludeContent && !this.props.excludeContent)
    ) {
      this.fetchPosts(hashtag);
    }
  }

  fetchPosts(hashtag: string, page = this.props.page) {
    if (this.props.isBlocked) {
      return;
    }

    return this.props.fetchHashtagPosts(hashtag, page);
  }

  loadMore = (page: number) => this.fetchPosts(this.props.hashtag, page);

  renderPosts() {
    const {
      currentPagePosts,
      allPosts,
      entityCount,
      showCreatePostAction,
      page,
      layoutType,
      showPagination,
      isHashtagLoading,
      isHashtagLoaded,
      PostListProGallery,
    } = this.props;

    if (layoutType && isLayoutProGallery(layoutType)) {
      return (
        <PostListProGallery
          pageStart={page + 1}
          entityCount={entityCount}
          loadMore={showPagination ? undefined : this.loadMore}
          allPosts={showPagination ? currentPagePosts : allPosts}
          currentPagePosts={currentPagePosts}
          isLoading={isHashtagLoading}
          showCreatePostAction={showCreatePostAction}
          section={SECTION_CATEGORY}
        />
      );
    }

    return (
      <PostList
        pageStart={page + 1}
        layoutType={layoutType}
        entityCount={entityCount}
        loadMore={showPagination ? undefined : this.loadMore}
        allPosts={showPagination ? currentPagePosts : allPosts}
        currentPagePosts={currentPagePosts}
        isLoading={isHashtagLoading}
        showCreatePostAction={showCreatePostAction}
      />
    );
  }

  renderPagination() {
    const { entityCount, pageSize, page, hashtagUrl, isSite, showPagination } = this.props;

    return showPagination ? (
      <Pagination
        dataHook="hashtag-pagination-container"
        page={page}
        pageSize={pageSize}
        entityCount={entityCount}
        createPageUrl={(page) => createPageUrl(page, hashtagUrl)}
        handleNavigation={handleNavigation('hashtag', isSite)}
      />
    ) : null;
  }

  render() {
    const { allPosts, isHashtagLoading, isHashtagLoaded } = this.props;
    const isLoading = isHashtagLoading && !isHashtagLoaded;
    return (
      <PostTitleTagProvider titleFontParam={stylesParams.cssTitleFontCategory} fallbackTagName="h2">
        <Page noSpacing>
          <Layout>
            <AnimatedLoader isLoading={isLoading && !allPosts.length}>
              {this.renderPosts()}
            </AnimatedLoader>
            {!isLoading ? this.renderPagination() : null}
          </Layout>
        </Page>
      </PostTitleTagProvider>
    );
  }
}

type OwnProps = {
  params: { hashtag: string };
  page: number;
  canSee: (action: string, entity: string) => boolean;
};

import { type Actions, type AppState } from '../../../common/types';

const mapRuntimeToProps = (state: AppState, ownProps: OwnProps, actions: Actions) => {
  const hashtag = get(ownProps, ['params', 'hashtag']);

  const sectionUrl = getSectionUrl(state);

  const showCreatePostAction =
    getIsMobile(state) && isCreatePostButtonVisible(state, ownProps.canSee);

  return {
    hashtag,
    hashtagUrl: getHashtagUrl(sectionUrl, hashtag),
    allPosts: hashtag ? getSortedCategoryPosts(state) : [],
    currentPagePosts: hashtag ? getPostsByPage(state, ownProps.page) : [],
    isHashtagLoading: getIsHashtagPostsLoading(state),
    isHashtagLoaded: getIsHashtagPostsLoaded(state),
    isSite: isSite(state),
    showCreatePostAction,
    excludeContent: isExcludePostContentSupported(state),
    setIsLoaded: actions.setIsLoaded,
    fetchHashtagPosts: (hashtag: string, page: number) =>
      actions.fetchHashtagPostsWithAdapterPromisified({
        page,
        includeContent: isPostContentRequired(state),
        hashtagOptions: { hashtag },
      }),
  };
};

export default flowRight(
  withPermissions,
  withComponents,
  withPaginationSettings(SECTION_CATEGORY),
  withLayoutProps(),
  withAuth,
  withTranslate,
  connect(mapRuntimeToProps),
)(HashtagPage);
